/**
 * WORK-ORDER-ITEMS-TABLE.JSX - Items Table Component for Work Order
 * 
 * SUMMARY:
 * Presentational component that renders the work order items table with line-level editing.
 * Shows task attribution, description, contract cost, amount, and removal actions for each line item.
 * 
 * PROPS:
 * @param {Array} selectedItems - Array of line items to display
 * @param {Array} attributionDropdownOptions - Options for attribution dropdown
 * @param {Function} handleAttributionChange - Handler for attribution changes
 * @param {Function} handleDescriptionChange - Handler for description edits
 * @param {Function} handleCostChange - Handler for cost/amount changes
 * @param {Function} handleRemoveLineItem - Handler for removing line items
 * @param {Function} handleAddManualItem - Handler for adding manual items
 */

import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TaskMultiSelect from "../../../../components/inputs/task-multi-select";
import CustomInput, { NumberInput } from "../../../../components/inputs/new-input";
import { numberFormat } from '../../../../util/currency-formatter';
import { findOptionInTree } from '../utils/work-order-helpers';

/**
 * Work Order items table component.
 * 
 * @component
 * @example
 * <WorkOrderItemsTable
 *   selectedItems={items}
 *   attributionDropdownOptions={options}
 *   handleAttributionChange={handleAttribution}
 *   handleDescriptionChange={handleDescription}
 * />
 */
const WorkOrderItemsTable = ({
  selectedItems,
  attributionDropdownOptions,
  handleAttributionChange,
  handleDescriptionChange,
  handleCostChange,
  handleRemoveLineItem,
  handleAddManualItem
}) => {
  return (
    <div className="material-list-block mt-3">
      <div className="table-responsive">
        <table className="table-create table-material tbl-fixed table table-bordered">
          <thead>
            <tr>
              <th>Task</th>
              <th>Description</th>
              <th>Contract Cost</th>
              <th>Amount</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {selectedItems.map((item, index) => (
              <tr key={item.isManual && item.manualId ? item.manualId : `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`}>
                <td>
                  <TaskMultiSelect
                    filterVariationsByStatus={true}

                    items={attributionDropdownOptions}
                    value={
                      item.taskId
                        ? findOptionInTree(
                          attributionDropdownOptions,
                          item.taskId
                        )
                        : null
                    }
                    onChangeHandler={(selectedOption) =>
                      handleAttributionChange(index, selectedOption)
                    }
                    placeholder="Select Task"
                    isMulti={false}
                    className="input-block-sm"
                    dropdownWidth="400px"
                    isSearchable={true}
                  />
                </td>
                <td>
                  <CustomInput
                    value={item.description ?? ''}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    className="input-block-sm h-auto"
                    dataCy={`input-line-description-${index}`}
                  />
                </td>
                <td>
                  {numberFormat(item.cost ?? 0)}
                </td>
                <td>
                  <NumberInput
                    className="input-block-sm w-auto"
                    customInputClass='text-right'
                    value={item.amount}
                    lable='Amount'
                    type='number'
                    name='quote'
                    data-cy="input-task-amount"
                    onChange={(e) => handleCostChange(index, "amount", e.target.value)}
                    onBlur={(e) => handleCostChange(index, "amount", e.target.value)}
                  />
                </td>
                <td className="text-right">
                  <Tooltip title="Remove this line" placement="top">
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRemoveLineItem(index)}
                      data-cy={`remove-line-item-${index}`}
                    >
                      <DeleteIcon className="deleteicon" />
                    </span>
                  </Tooltip>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={5} className="wo-add-line-cell">
                <div className="wo-add-line-cell-inner">
                  <button
                    type="button"
                    className="wo-add-line-in-table"
                    onClick={handleAddManualItem}
                    disabled={attributionDropdownOptions.length === 0}
                    data-cy="add-manual-item-button"
                  >
                    <AddIcon className="wo-add-line-icon" />
                    Add Item
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkOrderItemsTable;
